import { Box, Stack, Typography, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";

export interface EposPerformanceTrendSeries {
    label: string;
    data: (number | null)[];
    yAxisId?: string;
}

export interface EposPerformanceTrendAxis {
    id: string;
    scaleType: "linear";
    position: "left" | "right";
}

interface EposPerformanceTrendChartProps {
    periods: string[];
    series: EposPerformanceTrendSeries[];
    yAxis: EposPerformanceTrendAxis[];
    leftAxis: string;
    rightAxis?: string | null;
    colors?: string[];
}

export default function EposPerformanceTrendChart({
    periods,
    series,
    yAxis,
    leftAxis,
    rightAxis = null,
    colors
}: EposPerformanceTrendChartProps) {
    const theme = useTheme();
    const paletteColors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main
    ];
    const seriesColors = series.map((_, index) => colors?.[index] ?? paletteColors[index % paletteColors.length]);

    return (
        <>
            <Box
                role="list"
                aria-label="Performance chart legend"
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    columnGap: 2,
                    rowGap: 0.75,
                    px: 1,
                    mb: 1.5
                }}
            >
                {series.map((item, index) => (
                    <Stack
                        key={`${item.label}-${index}`}
                        role="listitem"
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 14,
                                height: 3,
                                borderRadius: 1,
                                flexShrink: 0,
                                backgroundColor: seriesColors[index]
                            }}
                        />
                        <Typography variant="caption">{item.label}</Typography>
                    </Stack>
                ))}
            </Box>
            <LineChart
                height={320}
                xAxis={[{
                    scaleType: "point",
                    data: periods,
                    valueFormatter: value => dayjs(value).format("D MMM"),
                    tickLabelInterval: () => true,
                    tickLabelStyle: { fontSize: 11, angle: -45, textAnchor: "end" }
                }]}
                series={series}
                colors={seriesColors}
                yAxis={yAxis}
                leftAxis={leftAxis}
                rightAxis={rightAxis}
                margin={{ left: 70, right: 70, top: 20, bottom: 52 }}
                slotProps={{ legend: { hidden: true } }}
            />
        </>
    );
}
